import React, { useState, useCallback } from 'react';
import { CameraLayoutSettingPanel } from 'components/views/organisms/CameraLayoutSettingPanel';
import { Vehicle } from 'core/models/vehicle';
import { useVehicle } from 'services/useVehicle';
import { SubPage } from 'components/views/molecules/SubPage';
import { SubmitForm } from '../../../../views/molecules/SubmitForm';
import { equalLayout } from 'core/models/layout';
import { useCameraLayout } from 'services/useCameraLayout';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { useMediaDeviceRepository } from 'services/useMediaDeviceRepository';
import { MediaDevice } from 'core/models/mediaDevice';
import { useScopeCheck } from 'services/useScope';

export type CameraLayoutSettingPageProps = {
    vehicleId: Vehicle.Id;
    projectId: Project.Id;
    environmentId: Environment.Id;
    onBack?: () => void;
}

export function CameraLayoutSettingPage(props: CameraLayoutSettingPageProps) {
    const { vehicleId, projectId, environmentId, onBack } = props;

    const [storedLayout, saveLayout] = useCameraLayout(vehicleId);
    const [editingLayout, updateEditingLayout] = useState(storedLayout);
    const vehicle = useVehicle(vehicleId, projectId, environmentId);
    const mediaDeviceRepos = useMediaDeviceRepository();
    const isUpdateMediaConfigAllowed = useScopeCheck('updateMediaConfigScope') ?? false;

    const getVideoConfig = useCallback(async (id: MediaDevice.Id) => {
        return await mediaDeviceRepos.getVideoParams(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const putVideoConfig = useCallback(async (id: MediaDevice.Id, config: Partial<MediaDevice.VideoConfig>) => {
        await mediaDeviceRepos.setVideoParams(id, projectId, environmentId, config);
        await mediaDeviceRepos.restartPublish(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const getAudioConfig = useCallback(async (id: MediaDevice.Id) => {
        return await mediaDeviceRepos.getAudioParams(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    const putAudioConfig = useCallback(async (id: MediaDevice.Id, config: Partial<MediaDevice.AudioConfig>) => {
        await mediaDeviceRepos.setAudioParams(id, projectId, environmentId, config);
        await mediaDeviceRepos.restartPublish(id, projectId, environmentId);
    }, [mediaDeviceRepos, projectId, environmentId]);

    return (
        <SubPage onClick={onBack}>
            <SubmitForm
                modified={!equalLayout(editingLayout, storedLayout)}
                cancellable={false}
                submitLabel='Apply'
                onSubmit={() => saveLayout(editingLayout)}>
                <CameraLayoutSettingPanel
                    vehicleId={vehicleId}
                    vehicleName={vehicle?.name ?? 'unknown vehicle'}
                    cameraDevices={vehicle?.cameras}
                    microphoneDevices={vehicle?.microphones}
                    layout={editingLayout}
                    onUpdate={updateEditingLayout}
                    getVideoConfig={getVideoConfig}
                    putVideoConfig={isUpdateMediaConfigAllowed ? putVideoConfig : undefined}
                    getAudioConfig={getAudioConfig}
                    putAudioConfig={isUpdateMediaConfigAllowed ? putAudioConfig : undefined}
                />
            </SubmitForm>
        </SubPage>
    );
}
